/* =========================================================
   URESSAY — theme.js
   One IIFE. Every feature registers into one rAF loop and is idempotent,
   so the Shopify theme editor can re-initialise a section at any time.
   ========================================================= */
(() => {
  'use strict';

  const S = window.UR || { routes: {}, strings: {} };
  const html = document.documentElement;
  const body = document.body;
  const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const flag = (k) => body.dataset['motion' + k.charAt(0).toUpperCase() + k.slice(1)] !== 'false';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const nrm = (v, a, b) => clamp((v - a) / (b - a), 0, 1);
  const SM = (p) => p * p * (3 - 2 * p);
  const EO = (p) => 1 - Math.pow(1 - p, 4);
  const damp = (k, dt) => 1 - Math.pow(1 - k, dt / 16.7);
  const once = (el, key) => { if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; };
  const pad2 = (n) => String(n).padStart(2, '0');
  // a value that walks from step to step and rests on every step (pinned scenes)
  const stair = (p, n, hold = 0.4) => { if (n < 2) return 0; const s = p * (n - 1), k = Math.min(Math.floor(s), n - 2), f = s - k; return k + SM(clamp((f - hold / 2) / (1 - hold), 0, 1)); };

  /* ---------- money ---------- */
  function money(cents) {
    const fmt = S.moneyFormat || '€{{amount_with_comma_separator}}';
    const n = (cents || 0) / 100;
    const fixed = (v, d, thou, dec) => { const parts = v.toFixed(d).split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou); return parts.length > 1 ? parts.join(dec) : parts[0]; };
    return fmt
      .replace('{{amount_no_decimals_with_comma_separator}}', fixed(n, 0, '.', ','))
      .replace('{{amount_with_comma_separator}}', fixed(n, 2, '.', ','))
      .replace('{{amount_with_apostrophe_separator}}', fixed(n, 2, "'", '.'))
      .replace('{{amount_with_space_separator}}', fixed(n, 2, ' ', ','))
      .replace('{{amount_no_decimals_with_space_separator}}', fixed(n, 0, ' ', ','))
      .replace('{{amount_no_decimals}}', fixed(n, 0, ',', '.'))
      .replace('{{amount}}', fixed(n, 2, ',', '.'));
  }

  /* ---------- toast ---------- */
  let toastT;
  function toast(msg) { const el = $('.toast'); if (!el) return; el.textContent = msg; el.classList.add('is-on'); clearTimeout(toastT); toastT = setTimeout(() => el.classList.remove('is-on'), 2600); }

  /* ---------- split text + reveals ---------- */
  function split(el) {
    if (!once(el, 'splitDone')) return;
    let i = 0;
    const walk = (node) => Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((w) => {
          if (!w) return;
          if (/^\s+$/.test(w)) { frag.appendChild(document.createTextNode(' ')); return; }
          const o = document.createElement('span'); o.className = 'w';
          const s = document.createElement('span'); s.textContent = w; s.style.setProperty('--i', i++);
          o.appendChild(s); frag.appendChild(o);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && !child.classList.contains('w')) walk(child);
    });
    walk(el);
  }
  const revealsOn = flag('reveals') && !RM;
  if (!revealsOn) html.classList.add('no-reveal');
  const io = revealsOn ? new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return; e.target.classList.add('in'); io.unobserve(e.target);
  }), { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }) : null;
  function observe(root = document) {
    $$('.rv, .clip, [data-split], .io', root).forEach((el) => {
      if (el.matches('[data-split]')) split(el);
      if (!revealsOn) { el.classList.add('in'); return; }
      if (el.classList.contains('in')) return;
      io.observe(el);
    });
  }

  /* =========================================================
     Frame loop
     ========================================================= */
  const loops = [];
  const resizers = [];
  let y = window.scrollY, lastY = y, vel = 0, W = innerWidth, H = innerHeight, lastT = 0, NOW = 0;
  function tick(t) {
    const dt = clamp(t - (lastT || t), 0, 64) || 16.7; lastT = t; NOW = t;
    y = window.scrollY; vel = lerp(vel, y - lastY, 0.12); lastY = y;
    for (let i = 0; i < loops.length; i++) {   // a loop returns false once its element is gone; one that throws is dropped so the rest keep running
      let r; try { r = loops[i](t, dt); } catch (e) { r = false; console.error(e); }
      if (r === false) loops.splice(i--, 1);
    }
  }
  function frame(t) { tick(t); requestAnimationFrame(frame); }
  let lastW = W;
  const onResize = () => { const wc = innerWidth !== lastW; lastW = W = innerWidth; H = innerHeight; if (wc || FINE) resizers.forEach((f) => f()); };   // touch: the address bar sliding never re-lays the page out
  addEventListener('resize', onResize, { passive: true });
  addEventListener('orientationchange', () => setTimeout(onResize, 300), { passive: true });
  // which elements are near the screen: their loops skip work (and layout reads) when they are far away — keeps phones smooth
  const visMap = new WeakMap();
  const visIO = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((e) => visMap.set(e.target, e.isIntersecting)), { rootMargin: '35% 0px' }) : null;
  const watch = (el) => { if (visIO && el && !visMap.has(el)) { visMap.set(el, true); visIO.observe(el); } return el; };
  const vis = (el) => visMap.get(el) !== false;
  // progress through a pinned section (0 at its top, 1 when its end reaches the bottom of the screen)
  const pinProgress = (el) => { const r = el.getBoundingClientRect(); return { p: clamp(-r.top / Math.max(1, r.height - H), 0, 1), on: r.bottom > -60 && r.top < H + 60, r }; };

  /* ---------- smooth (inertia) scroll ---------- */
  let smooth = false;
  if (flag('smooth') && FINE && !RM && !S.designMode) {
    smooth = true;
    let sT = window.scrollY, sC = sT, lastSelf = 0, active = false;
    const max = () => document.documentElement.scrollHeight - innerHeight;
    addEventListener('wheel', (e) => {
      if (body.classList.contains('is-locked') || e.ctrlKey) return;
      if (e.target.closest && e.target.closest('[data-native-scroll], textarea, select, .modal, .look, .drawer, .psearch, #shopify-pc__banner, #shopify-pc__prefs')) return;
      // a trackpad is already smooth: leave it native (Chrome/Safari report wheelDeltaY = -3 × deltaY for trackpads; Firefox scrolls them in pixels)
      const pad = e.wheelDeltaY ? e.wheelDeltaY === -3 * e.deltaY : e.deltaMode === 0;
      if (pad) { active = false; return; }
      e.preventDefault();
      if (!active) { sC = window.scrollY; sT = sC; }
      const d = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * innerHeight : e.deltaY;
      sT = clamp(sT + d, 0, max()); active = true;
    }, { passive: false });
    addEventListener('scroll', () => { if (performance.now() - lastSelf > 80) { sT = sC = window.scrollY; active = false; } }, { passive: true });
    loops.push((t, dt) => {
      if (!active) return;
      const diff = sT - sC;
      if (Math.abs(diff) < 0.35) { sC = sT; active = false; return; }
      sC += diff * damp(0.15, dt); lastSelf = performance.now(); window.scrollTo(0, sC);
    });
    S.scrollTo = (top) => { sC = window.scrollY; sT = clamp(top, 0, max()); active = true; };
  }
  function scrollToY(top) { if (smooth && S.scrollTo) S.scrollTo(top); else window.scrollTo({ top, behavior: RM ? 'auto' : 'smooth' }); }

  /* ---------- header ---------- */
  function initHeader() {
    const hdr = $('#SiteHeader');
    if (!hdr || !once(hdr, 'init')) return;
    let hidden = false, prev = window.scrollY;
    loops.push(() => {
      const dy = y - prev; prev = y;
      hdr.classList.toggle('is-scrolled', y > 30);
      if (hdr.dataset.sticky === 'false') return;
      const openMega = $('.has-mega.is-open', hdr);
      if (y > 160 && dy > 2 && !hidden && !body.classList.contains('is-locked') && !openMega) { hidden = true; hdr.classList.add('is-hidden'); }
      else if ((dy < -2 || y < 160) && hidden) { hidden = false; hdr.classList.remove('is-hidden'); }
    });
    $$('.has-mega', hdr).forEach((li) => {
      let t;
      const a = $('.hdr__link', li);
      li.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'mouse') return; clearTimeout(t); li.classList.add('is-open'); a.setAttribute('aria-expanded', 'true'); });
      li.addEventListener('pointerleave', () => { t = setTimeout(() => { li.classList.remove('is-open'); a.setAttribute('aria-expanded', 'false'); }, 160); });
      a.addEventListener('focus', () => li.classList.add('is-open'));
      li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget)) li.classList.remove('is-open'); });
    });
  }

  /* ---------- announcement + marquee ---------- */
  function initAnnouncement(root) { $$('.ann', root).forEach((el) => el.style.setProperty('--ann-speed', (el.dataset.speed || 45) + 's')); }
  function initMarquee(root) {
    $$('[data-marquee]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const track = $('.marq__track', el), group = $('.marq__group', el);
      if (!track || !group || RM) return;
      let x = 0, w = group.offsetWidth;
      resizers.push(() => { w = group.offsetWidth; });
      const base = () => w / (parseFloat(el.dataset.speed || 60)) / 60;
      watch(el);
      loops.push((t, dt) => {
        if (!el.isConnected) return false;
        if (!vis(el)) return;
        const r = el.getBoundingClientRect(); if (r.bottom < 0 || r.top > H) return;
        if (!w) w = group.offsetWidth;
        x -= (base() + Math.min(Math.abs(vel) * 0.35, 18)) * (dt / 16.7);
        if (x <= -w) x += w;
        track.style.transform = `translate3d(${x.toFixed(1)}px,0,0)`;
      });
    });
  }

  /* ---------- parallax ---------- */
  function initParallax(root) {
    if (RM) return;
    $$('[data-parallax]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const k = parseFloat(el.dataset.parallax || '0.08');
      watch(el);
      loops.push(() => {
        if (!el.isConnected) return false;
        if (!vis(el)) return;
        const r = el.getBoundingClientRect(); if (r.bottom < -100 || r.top > H + 100) return;
        el.style.transform = `translate3d(0,${((H / 2 - (r.top + r.height / 2)) * k).toFixed(1)}px,0)`;
      });
    });
  }

  /* ---------- manifesto words light up ---------- */
  function initScrollWords(root) {
    $$('[data-scroll-words]', root).forEach((el) => {
      if (!once(el, 'init')) return;
      const words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach((w) => { const s = document.createElement('span'); s.className = 'w'; s.textContent = w; el.appendChild(s); el.appendChild(document.createTextNode(' ')); });
      const spans = $$('.w', el);
      if (RM || !revealsOn) { spans.forEach((s) => s.classList.add('on')); return; }
      let lit = -1;
      watch(el);
      loops.push(() => {
        if (!el.isConnected) return false;
        if (!vis(el)) return;
        const r = el.getBoundingClientRect(); if (r.bottom < 0 || r.top > H) return;
        const start = H * 0.88, end = H * 0.32, p = clamp((start - r.top) / (start - end + r.height * 0.6), 0, 1), n = Math.round(p * spans.length);
        if (n !== lit) { lit = n; spans.forEach((s, i) => s.classList.toggle('on', i < n)); }
      });
    });
  }

  /* ---------- rail tilt (featured collection as a rail) ---------- */
  function initRails(root) {
    $$('[data-tilt-rail]', root).forEach((rail) => {
      if (!once(rail, 'init') || RM) return;
      let key = '';
      watch(rail);
      loops.push(() => {
        if (!rail.isConnected) return false;
        if (!vis(rail)) return;
        const r = rail.getBoundingClientRect(); if (r.bottom < 0 || r.top > H) return;
        const kids = Array.from(rail.children), mid = rail.scrollLeft + rail.clientWidth / 2, cw = rail.clientWidth;
        const ds = kids.map((k) => clamp((k.offsetLeft + k.offsetWidth / 2 - mid) / cw, -1.2, 1.2));
        const nk = ds.map((d) => d.toFixed(3)).join(); if (nk === key) return; key = nk;
        kids.forEach((k, i) => { k.style.transform = `rotateY(${(-ds[i] * 14).toFixed(2)}deg) translateZ(${(-Math.abs(ds[i]) * 40).toFixed(1)}px)`; });
      });
    });
  }

  /* =========================================================
     Turns. At rest the real photo shows. While a piece turns:
     1. its photoreal turn views (snippets/turn-frames: 45 / 90 / 135 degrees, made from the real photos) play between
        the real front and back photos, when the piece has them and they are loaded;
     2. else a real-3D render of the front + back pair (assets/g3d.js, loaded deferred) cross-fades in over the photo;
     3. the CSS flip stays as the fallback for every frame neither can draw (no views, no WebGL, not ready, reduced motion).
     ========================================================= */
  const HOVER = matchMedia('(hover: hover)').matches;
  const bezier = (x1, y1, x2, y2) => {
    const bz = (t, a, b) => 3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
    return (x) => { if (x <= 0) return 0; if (x >= 1) return 1; let lo = 0, hi = 1, t = x; for (let i = 0; i < 22; i++) { t = (lo + hi) / 2; if (bz(t, x1, x2) < x) lo = t; else hi = t; } return bz(t, y1, y2); };
  };
  // the theme's --ease, cubic-bezier(.19, 1, .22, 1), for the turns played in JS (3D render / CSS flip)
  const EASE = bezier(0.19, 1, 0.22, 1);
  // turns played through the photoreal views: a softer start, so each view is on screen for a few frames (with
  // --ease the first 135 degrees pass in ~150 ms and the views only flash); still settles slowly on the real photo
  const EASE_TF = bezier(0.3, 0.3, 0.45, 1);
  const G3 = () => (!RM && window.G3D && window.G3D.version ? window.G3D : null);
  // the engine is not in the layout: it is loaded on the first turn intent, or on idle after the page load on pages
  // that have turning pieces (window.UR.g3d = its asset URL; the offline preview inlines it as #g3d-src)
  let g3load = null;
  const g3debug = () => S.designMode || /[?&]g3d-debug\b/.test(location.search);
  function hookG3D(g) {
    if (g && !g.onfallback) g.onfallback = (reason, it, detail) => { if (g3debug()) console.info('[g3d] CSS turn kept:', reason, detail || '', (it && it.pair && it.pair.key) || ''); };
    return g;
  }
  function loadG3D() {
    if (G3()) return Promise.resolve(hookG3D(G3()));
    if (RM) return Promise.resolve(null);
    if (!g3load) {
      const inl = document.getElementById('g3d-src');
      if (!inl && !S.g3d) return Promise.resolve(null);
      g3load = new Promise((res) => {
        const sc = document.createElement('script');
        if (inl) { sc.textContent = inl.textContent; document.head.appendChild(sc); res(); return; }
        sc.src = S.g3d; sc.async = true; sc.onload = () => res(); sc.onerror = () => res();
        document.head.appendChild(sc);
      }).then(() => { const g = G3(); if (!g) console.warn('[g3d] engine failed to load: CSS turns'); return hookG3D(g); });
    }
    return g3load;
  }
  const g3failed = () => !!g3load && !G3();
  // may this page turn in real 3D? Before the engine is loaded: yes unless reduced motion (it decides on load)
  const g3on = () => {
    if (RM) return false;
    const g = G3(); if (!g) return !g3failed() && !!(S.g3d || document.getElementById('g3d-src'));
    try { return !!g.available(); } catch (e) { return false; }
  };
  let g3pre = false;
  function preloadG3D(root) {
    if (g3pre || RM || G3()) return;
    const sel = HOVER ? '.pdp__face--b, .g3__b, .card__turn--flip .card__img--b' : '.pdp__face--b, [data-set] .g3__b, [data-passport] .g3__b';
    if (!$$(sel, root).some((el) => !tfOf(el.closest('[data-tf1]')))) return;   // pieces with views never need the engine
    g3pre = true;
    const go = () => (window.requestIdleCallback || ((f) => setTimeout(f, 200)))(() => loadG3D(), { timeout: 4000 });
    if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
  }
  const srcOf = (im) => (im && (im.currentSrc || im.src)) || '';
  const loaded = (im) => new Promise((res) => {
    if (!im || (im.complete && im.naturalWidth)) { res(); return; }
    const k = () => { clearTimeout(tm); res(); }, tm = setTimeout(k, 6000);
    im.addEventListener('load', k, { once: true }); im.addEventListener('error', k, { once: true });
  });
  /* ---------- photoreal turn views ----------
     Keyframes: 0 = the real front photo, 45 / 90 / 135 = the views (transparent cut-outs framed exactly like the photo),
     180 = the real back photo. The nearest keyframe is held crisp; crossing the half-way point between two of them
     starts a short cross-fade of fixed duration (TF_FADE, however slowly or fast the piece turns: a double image is
     never on screen for longer than that), centred on the half-way point by starting it half a fade early at the
     current speed. The cross-fade is additive, so the piece never turns see-through, and the two silhouettes ease
     towards each other's width meanwhile (data-tf-geo), so the outline narrows / widens as one; a small rotateY on
     every layer, signed by the way the piece turns, keeps it moving so it reads as one turn. Past 180 the turn comes
     back through the same views: never mirrored, prints would read backwards. The layers sit on the photo's background
     colour where the photo has one (cards, product page, passport) and on nothing where the photo is a cut-out (tiles,
     set builder). Loaded on turn intent only; a turn waits up to 250 ms for them, then starts as the CSS flip and the
     views join it once decoded (the 3D engine is loaded only for views that fail, or hang for 2 s). */
  const TF = !RM, TF_FADE = 80, TF_HYS = 0.06, TF_ROT = 7;
  const tfGeo = (s) => { const n = String(s || '').trim().split(/\s+/).map(Number); return n.length === 10 && n.every((x) => x > 0 && x < 1.5) ? [0, 1, 2, 3, 4].map((j) => [n[2 * j], n[2 * j + 1]]) : null; };
  const tfOf = (el) => { const d = TF && el && el.dataset; return d && d.tf1 && d.tf2 && d.tf3 ? { urls: [d.tf1, d.tf2, d.tf3], dir: d.tfDir === 'r' ? 1 : -1, bg: d.tfBg || '', geo: tfGeo(d.tfGeo) } : null; };
  const imgURL = (u) => (S.img ? S.img(u) : u);                     // the offline preview maps asset keys to data URIs
  const tfAngle = (a) => { const e = ((a % 360) + 360) % 360; return e > 180 ? 360 - e : e; };
  const tfRest = (a) => { const e = tfAngle(a); return e < 0.25 || e > 179.75; };
  const photoBg = (im) => {                                          // data-tf-bg="auto": the photo's corner colour
    try { const c = document.createElement('canvas'); c.width = c.height = 1; const x = c.getContext('2d'); x.drawImage(im, 0, 0, 6, 6, 0, 0, 1, 1); const d = x.getImageData(0, 0, 1, 1).data; return d[3] > 250 ? `rgb(${d[0]},${d[1]},${d[2]})` : ''; } catch (e) { return ''; }
  };
  // A product photo printed on a page of the same grey (magazine pages, the full-screen viewer): paint the page the photo's
  // exact corner colour, so the photo's edge never shows as a line. Needs the <img> loaded with crossorigin="anonymous".
  const MATCH = '.pp__cover-pic, .pp__pic, .pdp__lb-slide img', FRAME = '.pp__cover, .pp__piece, .pdp__lb-slide';
  const bgMatch = (im) => { const c = photoBg(im), f = im.closest(FRAME); if (c && f) f.style.backgroundColor = c; };
  document.addEventListener('load', (e) => { const t = e.target; if (t.tagName === 'IMG' && t.matches(MATCH)) bgMatch(t); }, true);
  const bgMatchAll = () => $$(MATCH).forEach((im) => { if (im.complete && im.naturalWidth) bgMatch(im); });
  if (document.readyState === 'loading') addEventListener('DOMContentLoaded', bgMatchAll); else bgMatchAll();
  // garment centre + width (fractions of the picture width) of a picture, for views added without data-tf-geo
  // (the merchant's metafield views): columns that differ from the corner colour, or are opaque on a cut-out
  const measure = (im) => {
    const N = 96, c = document.createElement('canvas'); c.width = c.height = N;
    const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0, N, N);
    const d = x.getImageData(0, 0, N, N).data, cut = d[3] < 250; let lo = N, hi = -1;
    for (let i = 0; i < N; i++) {
      let n = 0;
      for (let j = 0; j < N; j++) { const o = (j * N + i) * 4; if (d[o + 3] > 64 && (cut || Math.max(Math.abs(d[o] - d[0]), Math.abs(d[o + 1] - d[1]), Math.abs(d[o + 2] - d[2])) > 12)) n++; }
      if (n >= 2) { if (i < lo) lo = i; hi = i; }
    }
    return hi - lo > N * 0.05 ? [(lo + hi + 1) / 2 / N, (hi + 1 - lo) / N] : null;
  };
  class Frames {
    constructor(spin, d) { this.s = spin; this.d = d; this.st = 0; this.p = null; this.el = null; this.ls = []; this.op = []; this.tr = []; this.P = -1; this.D = -1; this.K = -1; this.v = 0; this.la = null; this.pt = 0; this.join = 1; }
    // state: 0 not loaded, 1 loading, 2 ready, -1 failed / released
    prep() {
      if (this.st === 2) return Promise.resolve(true);
      if (this.st < 0) return Promise.resolve(false);
      if (this.p) return this.p;
      const s = this.s, fr = s.off === 180 ? s.b : s.f;
      this.st = 1;
      this.p = loaded(fr).then(() => {
        if (this.st !== 1 || !s.host.isConnected) throw new Error('gone');
        const el = document.createElement('span'), g = document.createElement('span'), fit = getComputedStyle(fr).objectFit || 'contain';
        el.className = 'tf'; el.setAttribute('aria-hidden', 'true'); g.className = 'tf__g'; el.appendChild(g);
        // the three views; the real photos at 0 and 180 stay on the card itself, so a hand-off to rest changes nothing
        this.ls = this.d.urls.map((u) => { const im = new Image(); im.className = 'tf__l'; im.alt = ''; im.draggable = false; im.style.objectFit = fit; if (!this.d.geo) im.crossOrigin = 'anonymous'; im.src = imgURL(u); g.appendChild(im); return im; });
        if (s.plate) { const bg = this.d.bg === 'auto' ? photoBg(fr) : this.d.bg ? `rgb(${this.d.bg})` : ''; if (bg) el.style.background = bg; }
        this.pv = Math.round(Math.max(900, s.host.clientWidth * 3)); this.cover = fit === 'cover';
        this.el = el; s.card.after(el);
        // decoded before the first turn (a decode() refusal on a loaded image is no reason to drop the views)
        return Promise.all(this.ls.map((im) => im.decode().catch(() => loaded(im).then(() => { if (!im.naturalWidth) throw new Error('view not loaded'); }))));
      }).then(() => {
        if (this.st !== 1) return false;
        if (!this.d.geo) { try { const bk = s.off === 180 ? s.f : s.b, gs = [fr, ...this.ls, bk].map(measure); if (gs.every(Boolean)) this.d.geo = gs; } catch (e) { /* cross-origin pictures: no morph */ } }
        this.st = 2; return true;
      })
        .catch((e) => { if (this.st === 1 && g3debug()) console.info('[turn] views kept out:', e && e.message, this.d.urls[0]); if (this.st === 1) this.release(); return false; })
        .then((r) => { this.p = null; return r; });
      return this.p;
    }
    show(join) {
      this.el.classList.add('is-on'); this.join = join ? 0 : 1; this.P = this.D = this.K = -1; this.v = 0; this.la = null; this.pt = 0; this.op = []; this.tr = []; this.ov = null;
      // the displayed size of the (square) pictures, for the silhouette morph: object-fit cover / contain of the box
      const bw = this.el.clientWidth, bh = this.el.clientHeight;
      this.sz = this.cover ? Math.max(bw, bh) : Math.min(bw, bh);
    }
    hide() { if (this.el) { this.el.classList.remove('is-on'); this.el.style.opacity = ''; } }
    // keyframe j at angle e (in the shared silhouette C / Wd while it cross-fades): its rotateY + morph
    pose(j, e, m, C, Wd) {
      const G = this.d.geo, r = this.d.dir * TF_ROT * clamp((e - 45 * j) / 45, -1, 1);
      let mo = '';
      if (G && m > 0 && m < 1) { const sx = Wd / G[j][1], dx = this.sz * ((C - 0.5) - (G[j][0] - 0.5) * sx); mo = `translateX(${dx.toFixed(1)}px) scaleX(${sx.toFixed(4)}) `; }
      return { r, mo };
    }
    // a: the real angle (0 = the front of the piece); the card holds the real photo of the nearer side.
    // Layers: card photo (front 0 / back 180) under the overlay (views + the photo's background colour), the two
    // added up (plus-lighter): at every point of a cross-fade the opacities sum to 1, so nothing turns see-through.
    draw(a) {
      // t: the real position in keyframes (0 front photo .. 4 back photo); D: the shown one, which moves to the target
      // keyframe K at a fixed pace (one cross-fade = TF_FADE ms; faster when it has fallen more than a step behind)
      const t = tfAngle(a) / 45, dt = this.pt ? clamp(NOW - this.pt, 0, 100) : 0;
      if (this.la !== null && dt > 0) this.v = lerp(this.v, (t - this.la) / dt, 0.5);     // keyframes per ms
      this.la = t; this.pt = NOW;
      const q = t + clamp(this.v * TF_FADE / 2, -0.35, 0.35);                              // half a fade ahead
      if (this.K < 0 || Math.abs(q - this.K) > 0.5 + TF_HYS) this.K = clamp(Math.round(q), 0, 4);
      if (this.D < 0) this.D = this.K;
      else { const st = dt / TF_FADE * Math.max(1, Math.abs(this.K - this.D)); this.D += clamp(this.K - this.D, -st, st); }
      this.P = this.D;
      const p = this.D, k = Math.min(Math.floor(p), 3), m = SM(clamp(p - k, 0, 1)), e = t * 45, G = this.d.geo;
      // the silhouette both layers share at this point of the cross-fade (centre C, width Wd, as fractions of the picture)
      const C = G ? lerp(G[k][0], G[k + 1][0], m) : 0, Wd = G ? lerp(G[k][1], G[k + 1][1], m) : 0;
      // overlay: the views (layer j = view j + 1)
      const ov = k === 0 ? m : k === 3 ? 1 - m : 1, ovs = (ov * this.join).toFixed(3);
      if (this.ov !== ovs) { this.ov = ovs; this.el.style.opacity = ovs === '1.000' ? '' : ovs; }
      for (let j = 1; j < 4; j++) {
        const o = k === 0 ? (j === 1 ? 1 : 0) : k === 3 ? (j === 3 ? 1 : 0) : j === k ? 1 - m : j === k + 1 ? m : 0;
        const os = o < 0.002 ? '0' : o > 0.998 ? '1' : o.toFixed(3), im = this.ls[j - 1];
        if (this.op[j] !== os) { this.op[j] = os; im.style.opacity = os; }
        if (os === '0' || ov < 0.002) continue;
        const { r, mo } = this.pose(j, e, m, C, Wd);
        const ts = Math.abs(r) < 0.005 && !mo ? 'none' : `perspective(${this.pv}px) rotateY(${r.toFixed(2)}deg) ${mo}`;
        if (this.tr[j] !== ts) { this.tr[j] = ts; im.style.transform = ts; }
      }
      // the card: the real photo of the nearer side, faded against the views, turning and morphing with them.
      // Joining a running turn (join 0 -> 1), the card eases from the CSS flip's angle into that pose.
      const s = this.s, front = k <= 1, j = k === 0 ? 0 : k === 3 ? 4 : -1, po = k === 0 ? 1 - m : k === 3 ? m : 0, J = this.join;
      const ph = front === (s.off !== 180) ? s.f : s.b, ot = ph === s.f ? s.b : s.f, base = front === (s.off !== 180) ? 0 : 180;
      const op = (v) => (v < 0.002 ? '0' : v > 0.998 ? '' : v.toFixed(3)), pos = op(lerp(1, po, J)), ots = op(1 - J);
      if (ph.style.opacity !== pos) ph.style.opacity = pos; if (ot.style.opacity !== ots) ot.style.opacity = ots;
      let r = 0, mo = '';
      if (j >= 0 && po > 0.002) ({ r, mo } = this.pose(j, e, m, C, Wd));
      let d = base + r;
      if (J < 1) { const c = s.deg + ((((d - s.deg) % 360) + 540) % 360) - 180; d = lerp(s.deg, c, J); }
      const ct = `${mo}rotateY(${d.toFixed(2)}deg)`;
      if (s.card.style.transform !== ct) s.card.style.transform = ct;
    }
    release() { this.st = -1; if (this.el) this.el.remove(); this.el = null; this.ls = []; }
  }

  const spins = new Set(), tweening = new Set();
  class Spin {
    // host: positioned box around the photos (gets the engine's canvas); card: the element the CSS flip rotates;
    // f / b: the <img> shown at 0 / 180 deg; off: 180 when f is the back of the piece (the engine wants the real front);
    // late: may switch to the views / 3D in the middle of a turn (continuous turns), with a short fade over the CSS flip;
    // tfd: the piece's photoreal turn views (tfOf), plate: lay them on the photo's background colour (false: cut-out photos)
    constructor(o) {
      Object.assign(this, { off: 0, late: false, quality: 'auto', persp: 1400, deg: 0, pairOK: true, plate: true, tfd: null }, o);
      this.it = null; this.p = null; this.on = false; this.use = false; this.fade = 1; this.ft = 0; this.tw = null; this.taken = false;
      this.tf = this.tfd ? new Frames(this, this.tfd) : null; this.tfOn = false; this.tfFade = 1; this.tfT = 0; this.mid = false;
      spins.add(this);
    }
    pics() { return !!(this.f && this.b && this.f.tagName === 'IMG' && this.b.tagName === 'IMG' && this.host.isConnected); }
    ok() { return this.pairOK && this.pics() && g3on(); }
    hasTF() { return !!(this.tf && this.tf.st >= 0 && this.pairOK && this.pics()); }
    tfReady() { return !!(this.tf && this.tf.st === 2 && this.pairOK); }
    setFrames(d) { this.tfOff(); if (this.tf) this.tf.release(); this.tf = d ? new Frames(this, d) : null; }
    ensure() {
      if (this.it) return this.it;
      const g = G3(); if (!g || !this.ok()) return null;
      const fr = this.off === 180 ? this.b : this.f, bk = this.off === 180 ? this.f : this.b;
      // URLs, not the elements: the engine re-reads the same (CORS-cached) file and keeps no reference to the DOM
      this.it = g.add({ host: this.host, fImg: srcOf(fr), bImg: srcOf(bk), photo: this.f, perspective: this.persp, quality: this.quality });
      return this.it;
    }
    // turn intent (hover, focus, touch, or the piece coming into view): load the views, else prepare the 3D pair;
    // resolves true when the turn is ready
    intent() {
      if (!this.hasTF()) return this.g3intent();
      const tf = this.tf;                                     // views replaced meanwhile (colour change): not a failure
      return tf.prep().then((ok) => ok || (this.tf === tf ? this.g3intent() : false));
    }
    g3intent() {
      if (this.p) return this.p;
      if (!this.ok()) return Promise.resolve(false);
      this.p = loadG3D().then((g) => (g && this.ok() ? Promise.all([loaded(this.f), loaded(this.b)]).then(() => { const it = this.ensure(); return it ? g.intent(it) : false; }) : false))
        .catch((e) => { console.warn('[g3d]', e); return false; }).then((r) => { this.p = null; return r; });
      return this.p;
    }
    ready() { if (this.tfReady()) return true; const g = G3(); return !!(g && this.it && !this.it.dead && g.ready(this.it)); }
    // start a turn once its views (else 3D) are ready, or after a short wait with what there is; returns a cancel function
    when(fn, cap = 350) {
      if (this.hasTF() && !this.tfReady()) {
        // views still loading after 250 ms: the turn starts as the CSS flip and the views join it (late spins) once
        // decoded. The 3D engine is loaded only when these views failed, or hang for 2 s; a turn asked for views that
        // were replaced meanwhile (a colour change) is dropped: the new colour's own call decides what plays
        const tf = this.tf; let done = false;
        const go = () => { if (done) return; done = true; clearTimeout(tm); if (this.tf === tf) fn(); };
        const fall = () => { if (this.tf === tf && tf.st !== 2 && this.ok()) this.g3intent(); };
        const tm = setTimeout(go, 250), slow = setTimeout(fall, 2000);
        tf.prep().then((ok) => { clearTimeout(slow); if (!ok) fall(); go(); });
        return () => { done = true; clearTimeout(tm); clearTimeout(slow); };
      }
      if (this.ready() || !this.ok()) { fn(); return () => {}; }
      let done = false; const go = () => { if (!done) { done = true; fn(); } };
      this.intent().then(go); const tm = setTimeout(go, cap);
      return () => { done = true; clearTimeout(tm); };
    }
    take() { if (this.taken) return; this.taken = true; this.card.style.transition = 'none'; this.css(this.deg); }
    css(deg) {
      this.card.style.transform = `rotateY(${deg.toFixed(2)}deg)`;
      if (this.f.style.opacity) this.f.style.opacity = ''; if (this.b && this.b.style.opacity) this.b.style.opacity = '';
    }
    // one call per frame with the card's angle: real photo at rest, the views (else the 3D render) while turning, CSS flip
    // when neither can draw. Views are decided once per turn (at its start), or join a running turn when `late`.
    draw(deg) {
      this.deg = deg;
      if (this.tf) {
        const a = deg + this.off;
        if (tfRest(a)) { if (this.tfOn) this.tfOff(); }
        // views join a running turn (late spins) over the CSS flip only, never over a 3D render already on screen:
        // that one finishes and the views take the next turn (a join would swap the render for the flat flip)
        else if (!this.tfOn && this.tfReady() && (!this.mid || (this.late && !this.use))) this.tfStart(this.mid);
        if (this.tfOn) { this.mid = true; this.tfDraw(a); return; }
        this.mid = !tfRest(a);
      }
      const g = G3(), m = g && this.it ? g.mix(deg) : 0;
      if (m <= 0) { if (this.on) this.end(); this.css(deg); return; }
      if (!this.on) { this.on = true; this.use = this.ready(); this.fade = 1; }                         // decided once per turn
      else if (!this.use && this.late && this.ready()) { this.use = true; this.fade = 0; this.ft = performance.now(); }
      const r = this.use ? g.frame(this.it, deg + this.off) : -1;
      if (r < 0) { this.css(deg); return; }
      if (this.fade < 1) {                                       // late switch: the render fades in over the CSS flip
        this.fade = clamp((performance.now() - this.ft) / 280, 0, 1);
        if (this.it.cv) this.it.cv.style.opacity = (r * SM(this.fade)).toFixed(3);
        this.css(deg); return;
      }
      const a = ((deg % 360) + 360) % 360, back = a > 90 && a < 270, ph = back ? this.b : this.f, ot = back ? this.f : this.b;
      this.card.style.transform = back ? 'rotateY(180deg)' : 'rotateY(0deg)';               // the CSS card holds its rest face
      ph.style.opacity = r >= 0.999 ? '0' : ''; if (ot.style.opacity) ot.style.opacity = '';
    }
    tfStart(join) {
      if (this.on) this.g3end();
      this.tfOn = true; this.tfFade = join ? 0 : 1; this.tfT = NOW; this.tf.show(join);
    }
    tfDraw(a) {
      if (this.tfFade < 1) {                                                 // joining a running turn: fade in over the CSS flip
        this.tfFade = clamp((NOW - this.tfT) / 300, 0, 1); this.tf.join = SM(this.tfFade);
      }
      this.tf.draw(a);
    }
    tfOff() {
      if (!this.tfOn) return;
      this.tfOn = false; this.tf.hide();
      if (this.f) this.f.style.opacity = ''; if (this.b) this.b.style.opacity = '';   // the card turns on with the next css()
    }
    busy() { return this.on || this.tfOn || this.mid; }
    g3end() {
      const g = G3(); if (g && this.it) g.idle(this.it);
      this.on = false; this.use = false; this.fade = 1;
      if (this.f) this.f.style.opacity = ''; if (this.b) this.b.style.opacity = '';
    }
    end() { this.tfOff(); this.g3end(); this.mid = false; }
    // tween to an angle (JS-played turns: hover, the passport button), the theme's easing and durations
    to(target, dur) {
      this.take();
      this.tw = { a: this.deg, b: target, t0: 0, d: dur * clamp(Math.abs(target - this.deg) / 180, 0.3, 1), ease: this.tfReady() ? EASE_TF : EASE };
      tweening.add(this);
    }
    reset() { this.end(); const g = G3(); if (g && this.it) g.remove(this.it); this.it = null; this.p = null; }
    release() { this.reset(); if (this.tf) this.tf.release(); tweening.delete(this); spins.delete(this); }
  }
  loops.push((t) => {
    tweening.forEach((s) => {
      if (!s.host.isConnected) { s.release(); return; }
      const w = s.tw; if (!w) { tweening.delete(s); return; }
      if (!w.t0) w.t0 = t;
      const k = clamp((t - w.t0) / w.d, 0, 1);
      s.draw(k >= 1 ? w.b : lerp(w.a, w.b, w.ease(k)));
      if (k >= 1) { s.tw = null; tweening.delete(s); }
    });
  });
  // hosts removed from the page (preview router swaps, editor reloads, recommendations re-render) give their engine instance back
  function sweepSpins(root) { spins.forEach((s) => { if (!s.host.isConnected || (root && root.contains(s.host))) s.release(); }); }
  let sweepT = 0;
  loops.push((t) => { if (t - sweepT > 2000) { sweepT = t; sweepSpins(); } });
  const g3Spin = (g, o) => new Spin(Object.assign({ host: g, card: $('.g3__in', g), f: $('.g3__f', g), b: $('.g3__b', g), off: g.dataset.g3First === 'back' ? 180 : 0, persp: 1600, quality: 'card', tfd: tfOf(g) }, o));
  // hover turns (product cards, collection tiles): JS takes the turn over from the CSS :hover rule while the engine is available.
  // A prepared piece turns at once. A fresh one waits a short dwell (a pointer passing over many cards prepares none), then
  // up to `cap` ms for its 3D pair; if that runs out the turn starts as the CSS flip and the render joins it once ready.
  function bindHoverTurn(zone, make, dur, views) {
    let s = null, dwell = 0, cancel = null;
    const get = () => { if (!s || !spins.has(s)) s = make(); return s; };
    const stop = () => { clearTimeout(dwell); if (cancel) { cancel(); cancel = null; } };
    zone.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch' || (!(s && s.taken) && !views && !g3on())) return;     // no views, no engine: the CSS :hover turn as before
      const sp = get(); sp.take(); stop();
      if (sp.ready()) { sp.to(180, dur); return; }
      dwell = setTimeout(() => { cancel = sp.when(() => { cancel = null; sp.to(180, dur); }, 250); }, 70);
    });
    zone.addEventListener('pointerleave', () => { stop(); if (s && s.taken) s.to(0, dur); });
  }
  function initTurns(root) {
    if (!HOVER || RM) return;   // touch screens: cards don't turn and tiles keep their tap state; reduced motion: CSS as before
    $$('.card__media', root).forEach((m) => {
      const turn = $('.card__turn--flip', m), link = $('.card__link', m);
      if (!turn || !link || !once(m, 'g3')) return;
      const tfd = tfOf(turn);
      bindHoverTurn(m, () => new Spin({ host: link, card: turn, f: $('.card__img--a', turn), b: $('.card__img--b', turn), off: turn.dataset.g3First === 'back' ? 180 : 0, persp: 1400, quality: 'card', late: true, tfd }), 1100, !!tfd);
    });
    $$('.tile', root).forEach((tile) => {
      const g = $('.tile__g3', tile);
      if (!g || !$('.g3__b', g) || !once(tile, 'g3')) return;
      bindHoverTurn(tile, () => g3Spin(g, { late: true, plate: false }), 1300, !!tfOf(g));
    });
  }

  /* =========================================================
     3D scenes
     ========================================================= */

  let heroStart = 0;
  // Hero collage: real photos float in depth around the wordmark; scrolling flies through them, the pointer or a finger tilts the room
  // [x, y] = offset from the centre in viewport fractions, z = depth (px), w = width as a fraction of the base size
  const COL_D = [[-.39, -.25, -140, .14], [.39, -.26, -60, .13], [-.38, .27, 110, .125], [.38, .26, 30, .13], [-.07, -.1, -1100, .17], [.12, .13, -1650, .16], [-.2, .05, -2100, .15], [.22, -.08, -2500, .14]];
  const COL_M = [[-.29, -.34, -60, .36], [.3, -.33, -240, .34], [-.29, .36, -120, .33], [.3, .355, 20, .32], [-.27, -.1, -1000, .34], [.27, .12, -1500, .32], [-.24, .08, -2000, .3], [.24, -.08, -2400, .3]];
  function initCollage(root) {
    $$('[data-collage]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const sc = $('[data-collage-scene]', sec), st = $('[data-collage-stage]', sec), ui = $('[data-collage-ui]', sec), figs = $$('[data-collage-item]', sec);
      let items = [], P = 1000, mob = null, mx = 0, my = 0, tx = 0, ty = 0, touch = null;
      let Hv = H;
      const build = () => {
        mob = W < 750; P = mob ? 760 : 1000; sc.style.perspective = P + 'px';
        // the hero starts under the announcement bar + header: lay the room out in the part you actually see
        Hv = H - clamp(sec.getBoundingClientRect().top + window.scrollY, 0, H * 0.3);
        const set = mob ? COL_M : COL_D, base = mob ? W : Math.min(W, Hv * 1.9);
        items = figs.map((el, i) => {
          const s = set[i % set.length], z = s[2] - Math.floor(i / set.length) * 2600, k = (P - z) / P;
          const ar = parseFloat(getComputedStyle(el).getPropertyValue('--ar')) || 0.664, w = s[3] * base * k, h = w / ar;
          el.style.width = w.toFixed(1) + 'px';
          const o = { el, x: s[0] * W * k - w / 2, y: s[1] * Hv * k - h / 2, z, i, f: -1 };
          el.style.transform = `translate3d(${o.x.toFixed(1)}px,${o.y.toFixed(1)}px,${z}px)`;
          return o;
        });
      };
      build(); resizers.push(() => { if (sec.isConnected) build(); });
      if (FINE) {
        sec.addEventListener('pointermove', (e) => { mx = (e.clientX / W - 0.5) * 2; my = (e.clientY / H - 0.5) * 2; }, { passive: true });
        sec.addEventListener('pointerleave', () => { mx = my = 0; });
      }
      // phones: the room follows the finger while it moves and springs back when it lets go (passive: scrolling stays native)
      sec.addEventListener('touchstart', (e) => { const p = e.touches[0]; touch = { x: p.clientX, y: p.clientY }; }, { passive: true });
      sec.addEventListener('touchmove', (e) => { if (!touch) return; const p = e.touches[0]; mx = clamp((p.clientX - touch.x) / (W * 0.4), -1, 1); my = clamp((p.clientY - touch.y) / (H * 0.6), -1, 1) * 0.6; }, { passive: true });
      const end = () => { touch = null; mx = my = 0; };
      sec.addEventListener('touchend', end, { passive: true }); sec.addEventListener('touchcancel', end, { passive: true });
      watch(sec);
      loops.push((t, dt) => {
        if (!sec.isConnected) return false;
        if (!vis(sec)) return;
        const r = sec.getBoundingClientRect(); if (r.bottom < 0 || r.top > H) return;
        const len = Math.max(1, r.height - H), pr = RM ? 0 : clamp(-r.top / len, 0, 1);
        const it = RM ? 1 : heroStart ? EO(clamp((t - heroStart) / 2400, 0, 1)) : 0;
        tx = lerp(tx, mx, damp(0.06, dt)); ty = lerp(ty, my, damp(0.06, dt));
        const drift = FINE || RM ? 0 : Math.sin(t / 2800) * 0.22;   // touch screens: the room breathes on its own
        const cam = pr * (mob ? 1500 : 2100) - (1 - it) * 700;
        st.style.transform = `translate3d(0,0,${cam.toFixed(1)}px) rotateX(${(ty * -3.5).toFixed(2)}deg) rotateY(${((tx + drift) * 5).toFixed(2)}deg)`;
        for (const o of items) {
          const zr = o.z + cam, near = P * (mob ? 0.32 : 0.5);
          let f = zr > near ? 1 - SM(clamp((zr - near) / (P * (mob ? 0.22 : 0.3)), 0, 1)) : 1;
          f *= clamp((it - o.i * 0.06) * 2.2, 0, 1);
          if (o.z < -900) f *= SM(clamp((pr - 0.03) / 0.2, 0, 1));   // the far photos arrive as you fly in
          f = Math.round(f * 100) / 100;
          if (f !== o.f) { o.f = f; o.el.style.opacity = f; o.el.style.visibility = f < 0.01 ? 'hidden' : 'visible'; o.el.style.pointerEvents = f < 0.6 ? 'none' : ''; }
        }
        const u = 1 - SM(clamp(pr / 0.26, 0, 1));
        ui.style.opacity = u.toFixed(3); ui.style.visibility = u < 0.01 ? 'hidden' : '';
        if (!mob) ui.style.transform = `translate(-50%,-50%) scale(${(0.92 + 0.08 * u).toFixed(4)})`;   // phones: opacity only (no re-raster while scrolling)
      });
    });
  }

  // Statements: hover a line and its tee follows the cursor. On touch screens scrolling plays the hover: the line crossing
  // the middle of the screen lights up and its tee floats in just above it (below it for the first line), never over the words.
  function initStatements(root) {
    $$('[data-statements]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const fl = $('[data-st-float]', sec), imgs = fl ? $$('.st__img', fl) : [];
      if (!fl) return;
      if (!FINE) {
        const list = $('.st__list', sec), rows = $$('[data-st-row]', sec);
        let cur = -1, y = 0, ty = 0, shown = false;
        watch(sec);
        loops.push((t, dt) => {
          if (!sec.isConnected) return false;
          if (!vis(sec)) return;
          const mid = innerHeight * 0.5; let best = -1, bd = 1e9;
          rows.forEach((a, k) => { const r = a.getBoundingClientRect(), d = Math.abs((r.top + r.bottom) / 2 - mid); if (d < bd && d < r.height * 0.75) { bd = d; best = k; } });
          if (best !== cur) {
            cur = best;
            rows.forEach((a, k) => a.parentNode.classList.toggle('is-on', k === cur));
            list.classList.toggle('is-scrub', cur > -1);
            if (cur > -1) imgs.forEach((im, k) => im.classList.toggle('is-on', k === cur));
            fl.classList.toggle('is-on', cur > -1);
            if (cur < 0) shown = false;
          }
          if (cur < 0) return;
          const s = sec.getBoundingClientRect(), r = rows[cur].getBoundingClientRect(), l = list.getBoundingClientRect(), h = fl.offsetHeight;
          ty = r.top - h - 10 < l.top ? r.bottom - s.top + 10 : r.top - s.top - h - 10;
          if (!shown) { y = ty; shown = true; }
          y = lerp(y, ty, damp(0.18, dt));
          fl.style.transform = `translate3d(${(l.right - s.left - fl.offsetWidth).toFixed(1)}px,${y.toFixed(1)}px,0) rotate(${clamp((ty - y) * 0.03, -4, 4).toFixed(2)}deg)`;
        });
        return;
      }
      let x = 0, y = 0, tx = 0, ty = 0, on = false, first = true;
      const place = () => { fl.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) rotate(${clamp((tx - x) * 0.04, -6, 6).toFixed(2)}deg)`; };
      sec.addEventListener('pointermove', (e) => {
        const r = sec.getBoundingClientRect(); tx = e.clientX - r.left + 28; ty = e.clientY - r.top - fl.offsetHeight * 0.5;
        if (first) { x = tx; y = ty; first = false; place(); }
      }, { passive: true });
      $$('[data-st-row]', sec).forEach((row) => {
        row.addEventListener('pointerenter', () => { const i = +row.dataset.stRow; imgs.forEach((im, k) => im.classList.toggle('is-on', k === i)); on = true; fl.classList.add('is-on'); });
        row.addEventListener('pointerleave', () => { on = false; fl.classList.remove('is-on'); });
      });
      loops.push((t, dt) => {
        if (!sec.isConnected) return false;
        if (!on && !fl.classList.contains('is-on')) { first = true; return; }
        x = lerp(x, tx, damp(0.16, dt)); y = lerp(y, ty, damp(0.16, dt)); place();
      });
    });
  }

  // Passport: one country per spread; click the page edges, swipe, use the arrows or the tabs
  function initPassport(root) {
    $$('[data-passport]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const book = $('[data-pp-book]', sec), leaves = $$('[data-pp-leaf]', book), N = leaves.length, tabs = $$('[data-pp-to]', sec), num = $('[data-pp-n]', sec), mob = $('[data-pp-mob]', sec);
      const tpl = $('[data-pp-pages]', sec), pages = tpl ? Array.from(tpl.content.querySelectorAll('[data-pp-page]')) : [];
      let f = 0, q = [], busy = false;
      const z = (j, fl) => (fl ? j + 1 : N - j);
      const paint = () => {
        book.classList.toggle('is-closed', f === 0); book.classList.toggle('is-end', f === N);
        tabs.forEach((t) => t.classList.toggle('is-on', +t.dataset.ppTo === f));
        if (num) num.textContent = f === 0 ? '—' : f === N ? '✓' : pad2(f);
        const prev = $('[data-pp-step="-1"]', sec), next = $('[data-pp-step="1"]', sec); if (prev) prev.disabled = f === 0; if (next) next.disabled = f === N;
        if (mob) { mob.innerHTML = ''; const pg = pages[f - 1]; if (pg) { mob.appendChild(pg.cloneNode(true)); observe(mob); } }
      };
      const turn = (j, fl) => { const el = leaves[j]; el.style.zIndex = 60; el.classList.toggle('is-flip', fl); clearTimeout(el._t); el._t = setTimeout(() => { el.style.zIndex = z(j, fl); }, RM ? 0 : 1000); };
      const run = () => { if (!q.length) { busy = false; return; } busy = true; const nf = q.shift(); if (nf > f) turn(f, true); else turn(f - 1, false); f = nf; paint(); setTimeout(run, RM ? 0 : 170); };
      const go = (target) => { target = clamp(target, 0, N); let k = q.length ? q[q.length - 1] : f; while (k !== target) { k += target > k ? 1 : -1; q.push(k); } if (!busy) run(); };
      sec._pp = { go, step: (d) => go((q.length ? q[q.length - 1] : f) + d) };
      leaves.forEach((el, j) => { el.style.zIndex = z(j, false); });
      const measure = () => { const vw = $('.pp__view', sec).offsetWidth, single = W < 990; const pw = single ? Math.min(vw, 420) : clamp(Math.min(vw / 2 - 6, H * 0.58), 250, 450); sec.style.setProperty('--pw', pw + 'px'); };
      measure(); resizers.push(measure); paint();
      sec.addEventListener('click', (e) => {
        const st = e.target.closest('[data-pp-step]'); if (st) { sec._pp.step(+st.dataset.ppStep); return; }
        const to = e.target.closest('[data-pp-to]'); if (to) { go(+to.dataset.ppTo); return; }
        const tb = e.target.closest('[data-pp-turn]'); if (tb) { const g = tb.parentElement.querySelector('.g3'); if (g) ppTurn(g); return; }
      });
      // turn intent: hovering, focusing or touching the button prepares the piece
      const ppSpin = (g) => { if (!g._spin || !spins.has(g._spin)) { if (!(g3on() || tfOf(g)) || !$('.g3__b', g)) return null; g._spin = g3Spin(g, { deg: g.classList.contains('is-back') ? 180 : 0 }); } return g._spin; };
      const ppTurn = (g) => {
        const s = ppSpin(g);
        if (s) s.take();                       // JS holds the angle first, so the class change starts no CSS transition
        g.classList.toggle('is-back');
        if (s) s.when(() => s.to(g.classList.contains('is-back') ? 180 : 0, 1200));
      };
      $$('[data-pp-turn]', sec).forEach((b) => ['pointerenter', 'focus', 'touchstart'].forEach((ev) => b.addEventListener(ev, () => {
        const g = b.parentElement.querySelector('.g3'), s = g && ppSpin(g); if (s) s.intent();
      }, { passive: true })));
      let sx = null;
      book.addEventListener('pointerdown', (e) => { if (e.target.closest('button, a, form')) return; sx = e.clientX; });
      book.addEventListener('pointerup', (e) => {
        if (sx == null) return; const dx = e.clientX - sx; sx = null;
        if (Math.abs(dx) > 36) { sec._pp.step(dx < 0 ? 1 : -1); return; }
        const r = book.getBoundingClientRect(), single = W < 990; sec._pp.step((e.clientX - r.left) / r.width > (single ? 0.75 : 0.5) ? 1 : -1);
      });
    });
  }

  // Shop the look: a rail of photos; drag it with the mouse, swipe on touch, or use the arrows; tap one to shop it
  function initCircle(root) {
    $$('[data-circle]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const rail = $('[data-circle-scene]', sec), bar = $('[data-circle-bar]', sec), modal = $('[data-look-modal]', sec), items = modal ? $$('[data-look-item]', modal) : [];
      let down = null, moved = 0, cur = 0;
      const upd = () => {
        if (!bar || !rail) return;
        const fr = clamp(rail.clientWidth / Math.max(1, rail.scrollWidth), 0.06, 1), max = rail.scrollWidth - rail.clientWidth;
        bar.style.width = (fr * 100).toFixed(2) + '%'; bar.style.transform = `translateX(${((max > 0 ? rail.scrollLeft / max : 0) * (1 - fr) / fr * 100).toFixed(2)}%)`;
      };
      if (rail) {
        rail.addEventListener('scroll', upd, { passive: true }); resizers.push(upd); upd();
        rail.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button > 0) return; down = { x: e.clientX, s: rail.scrollLeft }; moved = 0; });
        addEventListener('pointermove', (e) => { if (!down) return; const dx = e.clientX - down.x; moved = Math.max(moved, Math.abs(dx)); if (moved > 5) { rail.classList.add('is-grab'); rail.scrollLeft = down.s - dx; } }, { passive: true });
        const up = () => { if (!down) return; down = null; rail.classList.remove('is-grab'); };
        addEventListener('pointerup', up); addEventListener('pointercancel', up);
        rail.addEventListener('click', (e) => { if (moved > 6) { e.preventDefault(); e.stopPropagation(); moved = 0; } }, true);
        rail.addEventListener('dragstart', (e) => e.preventDefault());
      }
      $$('[data-circle-step]', sec).forEach((btn) => btn.addEventListener('click', () => {
        const c = rail && rail.firstElementChild; if (!c) return;
        rail.scrollBy({ left: +btn.dataset.circleStep * (c.offsetWidth + 12) * (W < 990 ? 1 : 2), behavior: RM ? 'auto' : 'smooth' });
      }));
      const show = (i) => { cur = (i + items.length) % items.length; items.forEach((it, k) => { it.hidden = k !== cur; }); };
      sec.addEventListener('click', (e) => {
        const c = e.target.closest('[data-look]'); if (c && modal) { show(+c.dataset.look); openOverlay(modal); return; }
        const st = e.target.closest('[data-look-step]'); if (st) { show(cur + +st.dataset.lookStep); return; }
        if (e.target.closest('[data-close-look]')) closeOverlay(modal);
      });
      // the look dialog lives inside the section; move it to <body> so no transformed ancestor traps it
      if (modal && !S.designMode) { modal.id = modal.id || 'Look'; $$('body > [data-look-modal]').forEach((m) => { if (m !== modal && m.id === modal.id) m.remove(); }); body.appendChild(modal); modal.addEventListener('click', (e) => {
        const st = e.target.closest('[data-look-step]'); if (st) { show(cur + +st.dataset.lookStep); return; }
        if (e.target.closest('[data-close-look]')) closeOverlay(modal);
      }); }
    });
  }

  // Set builder: the outfit turns on a stand; pick the look, both sizes, add both pieces in one request
  function initSet(root) {
    $$('[data-set]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const figs = $$('[data-set-fig]', sec), tabs = $$('[data-set-tab]', sec), panels = $$('[data-set-panel]', sec);
      let cur = 0;
      sec.addEventListener('click', async (e) => {
        const tab = e.target.closest('[data-set-tab]');
        if (tab) {
          cur = +tab.dataset.setTab;
          tabs.forEach((x, i) => x.classList.toggle('is-on', i === cur)); panels.forEach((x, i) => x.classList.toggle('is-on', i === cur)); figs.forEach((x, i) => x.classList.toggle('is-on', i === cur));
          return;
        }
        const size = e.target.closest('.sizes .size');
        if (size && !size.disabled) { $$('.size', size.parentElement).forEach((b) => b.classList.toggle('is-on', b === size)); return; }
        const add = e.target.closest('[data-set-add]');
        if (add) {
          const panel = add.closest('[data-set-panel]'), rows = $$('[data-set-piece]', panel), ids = [];
          for (const row of rows) { const on = $('.size.is-on', row); if (!on) { const g = $('.sizes', row); g.classList.remove('shake'); void g.offsetWidth; g.classList.add('shake'); toast(S.strings.chooseSize || 'Choose a size'); return; } ids.push(on.dataset.variant); }
          add.classList.add('is-loading');
          try {
            const res = await fetch(`${S.routes.cartAdd || '/cart/add'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: ids.map((id) => ({ id: +id, quantity: 1 })), sections: cartSectionIds(), sections_url: location.pathname }) });
            const data = await res.json(); if (data.status || data.errors) throw new Error(data.description || data.message || S.strings.cartError);
            applySections(data.sections); bump(); openCart(); toast(S.strings.added || 'Added');
          } catch (ex) { toast(ex.message || S.strings.cartError); } finally { add.classList.remove('is-loading'); }
        }
      });
    });
  }

  // Story · the name (pinned): U steps aside, RESSAY turns in the mirror and reads YASSER, then both meet again
  function initNameScene(root) {
    $$('[data-name-scene]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const wd = $('.nm__wd', sec), ls = $$('.lt', wd), caps = $$('.nm__cap', sec), bar = $('[data-name-bar]', sec);
      const pos = ls.map((l) => ({ x: parseFloat(l.style.left), w: parseFloat(l.style.width) }));   // in % of the wordmark
      const order = [6, 5, 4, 3, 2, 1], T = {}; let x = pos[1].x; order.forEach((i) => { T[i] = x; x += pos[i].w - 0.15; });
      watch(sec);
      loops.push(() => {
        if (!sec.isConnected) return false;
        if (!vis(sec)) return;
        const { p, on } = pinProgress(sec); if (!on) return;
        const u = wd.offsetWidth / 100;
        const a = SM(nrm(p, 0.14, 0.26)) - SM(nrm(p, 0.36, 0.44)), b = SM(nrm(p, 0.44, 0.62)) - SM(nrm(p, 0.76, 0.88)), back = SM(nrm(p, 0.86, 0.96));
        const fu = SM(nrm(p, 0.4, 0.48)) - SM(nrm(p, 0.8, 0.9));
        ls.forEach((el, i) => {
          if (i === 0) { el.style.transform = `translate3d(${(-(a * 2.2 + fu * 3) * u).toFixed(1)}px,0,0) scale(${(1 + a * 0.08).toFixed(4)})`; el.style.opacity = (1 - fu * 0.82).toFixed(3); return; }
          const k = order.indexOf(i), s = SM(clamp(b * 1.5 - k * 0.1, 0, 1)), dx = (T[i] - pos[i].x) * u * s, zz = Math.sin(s * Math.PI) * (k % 2 ? -1 : 1) * 260 * (W < 750 ? 0.5 : 1);
          el.style.transform = `translate3d(${(dx + a * 5 * u).toFixed(1)}px,${(-Math.sin(s * Math.PI) * 18).toFixed(1)}px,${zz.toFixed(1)}px) rotateY(${(s * 360 * (k % 2 ? -1 : 1)).toFixed(2)}deg)`;
          el.style.opacity = (1 - a * 0.84).toFixed(3);
        });
        const cv = [1 - SM(nrm(p, 0.1, 0.16)), a, b > 0.55 ? SM(nrm(b, 0.55, 0.9)) : 0, back];
        caps.forEach((c, i) => { const v = clamp(cv[i], 0, 1); c.style.opacity = v.toFixed(3); c.style.transform = `translate3d(0,${((1 - v) * 26).toFixed(1)}px,0)`; });
        wd.classList.toggle('is-y', b > 0.6);
        if (bar) bar.style.transform = `scaleX(${p.toFixed(4)})`;
      });
    });
  }

  // Story · belief (pinned): three masks peel off the real photo, each word gets crossed out
  function initMasks(root) {
    $$('[data-masks]', root).forEach((sec) => {
      if (!once(sec, 'init')) return;
      const ms = $$('.bl__m', sec).reverse(), words = $$('.bl__w li', sec), list = $('.bl__w', sec), end = $('.bl__end', sec), tag = $('.bl__tag', sec), bar = $('[data-masks-bar]', sec);
      watch(sec);
      loops.push(() => {
        if (!sec.isConnected) return false;
        if (!vis(sec)) return;
        const { p, on } = pinProgress(sec); if (!on) return;
        ms.forEach((m, i) => {
          const q = SM(nrm(p, 0.12 + i * 0.2, 0.3 + i * 0.2));
          m.style.transform = `translate3d(${(-q * 26).toFixed(2)}%,${(-q * 34).toFixed(2)}%,${(q * 420).toFixed(1)}px) rotateX(${(-q * 48).toFixed(2)}deg) rotateZ(${(-q * 14).toFixed(2)}deg)`;
          m.style.opacity = (1 - SM(nrm(q, 0.55, 1))).toFixed(3); m.style.visibility = q >= 1 ? 'hidden' : '';
          if (words[i]) words[i].style.setProperty('--q', q.toFixed(3));
        });
        const e = SM(nrm(p, 0.74, 0.86));
        if (end) { end.style.opacity = e.toFixed(3); end.style.transform = `translate3d(0,${((1 - e) * 24).toFixed(1)}px,0)`; }
        if (tag) tag.classList.toggle('is-vis', e > 0.5);
        if (list && W < 750) list.style.opacity = (1 - e).toFixed(3);
        if (bar) bar.style.transform = `scaleX(${p.toFixed(4)})`;
      });
    });
  }


  /* =========================================================
     Overlays: menu / search / cart drawer / modals
     ========================================================= */
  let lastFocus = null;
  function lock() { body.classList.add('is-locked'); }
  function unlock() { if (!$('.mm.is-open, .drawer.is-open, .psearch.is-open, .modal.is-open, .look.is-open')) body.classList.remove('is-locked'); }
  function openOverlay(el, focusSel) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); lock();
    const f = focusSel ? $(focusSel, el) : $('.icon-btn, button, a, input', el);
    setTimeout(() => f && f.focus({ preventScroll: true }), 250);
  }
  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); unlock();
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') $$('.mm.is-open, .drawer.is-open, .psearch.is-open, .modal.is-open, .look.is-open').forEach(closeOverlay); });

  /* ---------- size guide (the table comes from the product description) ---------- */
  let unit = 'cm';
  try { unit = localStorage.getItem('ur:unit') || 'cm'; } catch (e) { /* no storage */ }
  function sizeGuide(src) {
    const modal = $('#SizeGuide'); if (!modal || !src) return;
    const rows = Array.from(src.content.querySelectorAll('tr')).map((tr) => Array.from(tr.children).map((td) => td.textContent.trim()));
    if (rows.length < 3) return;
    const sizes = rows[0].filter(Boolean), units = rows[1].slice(1), ui = units.findIndex((u) => u.toLowerCase().startsWith(unit === 'cm' ? 'cm' : 'in'));
    const pick = ui < 0 ? 0 : ui % 2;
    const bodyHtml = '<table><thead><tr><th></th>' + sizes.map((s) => `<th>${s}</th>`).join('') + '</tr></thead><tbody>' +
      rows.slice(2).map((r) => '<tr><th>' + r[0] + '</th>' + r.slice(1).filter((_, i) => i % 2 === pick).map((v) => `<td>${v}</td>`).join('') + '</tr>').join('') + '</tbody></table>';
    $('[data-size-guide-body]', modal).innerHTML = bodyHtml;
    $('#SizeGuideTitle').textContent = src.dataset.title || '';
    $$('[data-unit]', modal).forEach((b) => b.classList.toggle('is-on', b.dataset.unit === unit));
    modal._src = src;
    if (!modal.classList.contains('is-open')) openOverlay(modal, '[data-unit].is-on');
  }

  /* ---------- cart ---------- */
  const cartType = S.cartType || 'drawer';
  const drawer = () => $('#CartDrawer');
  function openCart() { const d = drawer(); if (d) openOverlay(d, '.icon-btn'); else location.href = S.routes.cart || '/cart'; }
  function bump() { const b = $('#CartBubble'); if (!b) return; b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); }
  function applySections(sections) {
    if (!sections) return;
    const parser = new DOMParser();
    if (sections['cart-drawer'] && drawer()) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html'), fresh = doc.querySelector('cart-drawer'), cur = drawer();
      if (fresh && cur) { cur.innerHTML = fresh.innerHTML; cur.className = fresh.className + (cur.classList.contains('is-open') ? ' is-open' : ''); }
    }
    if (sections['cart-icon-bubble']) {
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html'), b = $('#CartBubble'), inner = doc.querySelector('.shopify-section') || doc.body;
      if (b) b.innerHTML = inner.innerHTML;
    }
    const page = $('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const doc = parser.parseFromString(sections[page.dataset.sectionId], 'text/html'), fresh = doc.querySelector('[data-cart-page]');
      if (fresh) { page.innerHTML = fresh.innerHTML; observe(page); }
    }
  }
  function cartSectionIds() {
    const ids = ['cart-icon-bubble'];
    if (drawer()) ids.push('cart-drawer');
    const page = $('[data-cart-page]'); if (page) ids.push(page.dataset.sectionId);
    return ids;
  }
  async function changeLine(line, quantity, lineEl) {
    if (lineEl) lineEl.classList.add('is-loading');
    try {
      const res = await fetch(`${S.routes.cartChange || '/cart/change'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: location.pathname }) });
      const data = await res.json(); if (data.errors || data.description) throw new Error(data.errors || data.description);
      applySections(data.sections);
    } catch (err) {
      if (lineEl) { lineEl.classList.remove('is-loading'); const e = $('.line__error', lineEl); if (e) { e.textContent = err.message || S.strings.cartError; e.hidden = false; } } else toast(S.strings.cartError);
    }
  }
  let noteT;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteT);
    noteT = setTimeout(() => fetch(`${S.routes.cartUpdate || '/cart/update'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) }), 500);
  });

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.bound) return;
      this.bound = true;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      if (cartType !== 'drawer' && !drawer()) return; // native submit → cart page
      e.preventDefault();
      const sub = e.submitter, btn = sub || this.form.querySelector('[type="submit"]');
      const err = this.querySelector('.form__error');
      if (btn && btn.hasAttribute('disabled')) return;
      btn && btn.classList.add('is-loading');
      if (err) err.hidden = true;
      const fd = new FormData(this.form);
      if (sub && sub.name === 'id' && sub.value) fd.set('id', sub.value);   // quick add: every size button carries its own variant id
      fd.append('sections', cartSectionIds().join(','));
      fd.append('sections_url', location.pathname);
      try {
        const res = await fetch(`${S.routes.cartAdd || '/cart/add'}`, { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' } });
        const data = await res.json();
        if (data.status || data.errors) throw new Error(data.description || data.message || S.strings.cartError);
        applySections(data.sections); bump();
        const look = this.closest('.look'); if (look) closeOverlay(look);
        openCart(); toast(S.strings.added || 'Added');
      } catch (ex) {
        if (err) { err.textContent = ex.message; err.hidden = false; } else toast(ex.message);
      } finally { btn && btn.classList.remove('is-loading'); }
    }
  }
  customElements.get('product-form') || customElements.define('product-form', ProductForm);
  class CartDrawer extends HTMLElement { connectedCallback() { /* markup only; behaviour is delegated */ } }
  customElements.get('cart-drawer') || customElements.define('cart-drawer', CartDrawer);
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const v = parseInt(this.input.value || '0', 10) + (b.name === 'plus' ? 1 : -1);
        const min = parseInt(this.input.min || '0', 10), max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        this.input.value = clamp(v, min, max);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  customElements.get('quantity-input') || customElements.define('quantity-input', QuantityInput);
  document.addEventListener('change', (e) => {
    const inp = e.target;
    if (inp.matches('.qty__input[data-line]')) changeLine(parseInt(inp.dataset.line, 10), parseInt(inp.value || '0', 10), inp.closest('.line'));
    if (inp.matches('[data-localization-select]')) inp.closest('form').submit();
    if (inp.matches('[data-sort-select]')) { const url = new URL(location.href); url.searchParams.set('sort_by', inp.value); url.searchParams.delete('page'); location.href = url.toString(); }
  });

  /* ---------- product page: the store's own photos, never cropped; swipe / arrows / thumbnails, tap to view full screen ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      this.track = $('[data-track]', this); this.slides = $$('[data-slide]', this); this.thumbs = $$('[data-thumb]', this);
      this.count = $('[data-count]', this); this.lb = $('[data-lightbox]', this); this.cur = -1;
      if (!this.track) return;
      const start = clamp(+this.dataset.start || 0, 0, Math.max(0, this.slides.length - 1));
      this.go(start, true);
      let t;
      this.track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.sync(), 60); }, { passive: true });
      this.thumbs.forEach((b) => b.addEventListener('click', () => this.go(+b.dataset.thumb)));
      $$('[data-step]', this).forEach((b) => b.addEventListener('click', () => this.go(this.cur + +b.dataset.step)));
      this.track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.go(this.cur + 1); if (e.key === 'ArrowLeft') this.go(this.cur - 1); });
      $$('[data-zoom]', this).forEach((b) => b.addEventListener('click', () => this.open(+b.dataset.zoom)));
      if (this.lb) {
        $('[data-lb-close]', this.lb).addEventListener('click', () => this.close());
        this.lb.addEventListener('click', (e) => { if (e.target === this.lb || e.target.classList.contains('pdp__lb-slide')) this.close(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.lb.classList.contains('is-open')) this.close(); });
      }
      resizers.push(() => { if (this.isConnected && this.cur > -1) this.go(this.cur, true); });
    }
    go(i, instant) {
      const n = this.slides.length; if (!n) return;
      i = ((i % n) + n) % n;
      const sl = this.slides[i];
      this.track.scrollTo({ left: sl.offsetLeft - this.track.offsetLeft, behavior: instant || RM ? 'auto' : 'smooth' });
      this.mark(i);
    }
    sync() {
      const w = this.track.clientWidth || 1, i = Math.round(this.track.scrollLeft / w);
      if (i !== this.cur) this.mark(clamp(i, 0, this.slides.length - 1));
    }
    mark(i) {
      this.cur = i;
      const sl = this.slides[i]; if (sl && sl.offsetHeight) this.track.style.height = sl.offsetHeight + 'px';   // the stage takes the shape of the photo on show: no bands, nothing cropped
      this.thumbs.forEach((b, k) => b.classList.toggle('is-active', k === i));
      const tb = this.thumbs[i]; if (tb && tb.parentElement) { const p = tb.parentElement; p.scrollTo({ left: tb.offsetLeft - p.clientWidth / 2 + tb.offsetWidth / 2, behavior: 'smooth' }); }
      if (this.count) this.count.textContent = i + 1;
    }
    open(i) {
      if (!this.lb) return;
      if (this.lb.parentElement !== body && !S.designMode) body.appendChild(this.lb);   // escape any transformed ancestor
      $$('[data-lb-src]', this.lb).forEach((im) => { if (!im.getAttribute('src')) im.src = (window.__IMG && window.__IMG[im.dataset.lbSrc]) || im.dataset.lbSrc; });
      openOverlay(this.lb, '[data-lb-close]');
      const tr = $('[data-lb-track]', this.lb), f = tr && tr.children[i];
      if (f) tr.scrollTo({ left: f.offsetLeft, behavior: 'auto' });
    }
    close() { if (this.lb) closeOverlay(this.lb); }
    goToMedia(id) { const k = this.slides.findIndex((s) => s.dataset.mediaId === String(id)); if (k > -1) this.go(k); }
    disconnectedCallback() { if (this.lb && this.lb.parentElement === body) this.lb.remove(); }
  }
  customElements.get('product-gallery') || customElements.define('product-gallery', ProductGallery);

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.bound) return; this.bound = true;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.addEventListener('change', () => this.update());
    }
    selected() {
      const values = [];
      this.querySelectorAll('fieldset').forEach((fs) => {
        const r = fs.querySelector('input:checked'); values.push(r ? r.value : null);
        const lab = fs.querySelector('[data-selected-value]'); if (lab) lab.textContent = values[values.length - 1] || '';
      });
      return values;
    }
    update() {
      const values = this.selected();
      const v = this.variants.find((x) => x.options.every((o, i) => o === values[i]));
      const section = this.closest('[data-product]');
      const form = section && section.querySelector('form[data-type="add-to-cart-form"]');
      const btn = section && section.querySelector('[data-add-button]'), lab = section && section.querySelector('[data-add-label]'), priceEl = section && section.querySelector('[data-add-price]');
      const priceBox = section && section.querySelector('.pdp__price .price'), barPrice = section && section.querySelector('[data-bar-price]');
      if (!v) { if (btn) btn.setAttribute('disabled', ''); if (lab) lab.textContent = S.strings.unavailable; return; }
      if (form) form.querySelector('[name="id"]').value = v.id;
      if (btn) { if (v.available) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', ''); }
      if (lab) lab.textContent = v.available ? S.strings.addToCart : S.strings.soldOut;
      if (priceEl) priceEl.textContent = money(v.price);
      if (barPrice) barPrice.textContent = money(v.price);
      if (priceBox) { const cur = priceBox.querySelector('.price__current'); if (cur) cur.textContent = money(v.price); }
      section.dataset.chosen = '1';
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url.toString());
      const gallery = section && section.querySelector('product-gallery');
      if (gallery && v.featured_media && gallery.goToMedia) gallery.goToMedia(v.featured_media.id);
    }
  }
  customElements.get('variant-picker') || customElements.define('variant-picker', VariantPicker);

  function initProductBar(root) {
    $$('.pdp', root).forEach((pdp) => {
      if (!once(pdp, 'barInit')) return;
      const bar = $('[data-sticky-bar]', pdp), buy = $('.pdp__buy', pdp);
      if (!bar || !buy || !('IntersectionObserver' in window)) return;
      new IntersectionObserver((es) => es.forEach((e) => bar.classList.toggle('is-vis', !e.isIntersecting && e.boundingClientRect.top < 0))).observe(buy);
      $('[data-bar-go]', bar).addEventListener('click', () => {
        const sizes = $('.pdp__variants', pdp); if (sizes) scrollToY(sizes.getBoundingClientRect().top + window.scrollY - H * 0.3);
      });
    });
  }

  /* ---------- predictive search ---------- */
  function initSearch() {
    const modal = $('#SearchModal');
    if (!modal || !once(modal, 'init')) return;
    const input = $('[data-search-input]', modal), results = $('[data-search-results]', modal);
    let t, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim(); if (q.length < 2) return;
      t = setTimeout(async () => {
        ctrl && ctrl.abort(); ctrl = new AbortController();
        try {
          const url = `${S.routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url, { signal: ctrl.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html'), fresh = doc.querySelector('.psearch__results');
          if (fresh) results.innerHTML = fresh.outerHTML;
        } catch (e) { /* aborted */ }
      }, 220);
    });
  }

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target, hit = (s) => t.closest(s);
    if (hit('[data-open-menu]')) { e.preventDefault(); openOverlay($('#MobileMenu'), '.mm__link'); }
    else if (hit('[data-close-menu]')) { e.preventDefault(); closeOverlay($('#MobileMenu')); }
    else if (hit('[data-open-search]')) { e.preventDefault(); openOverlay($('#SearchModal'), '[data-search-input]'); }
    else if (hit('[data-close-search]')) { e.preventDefault(); closeOverlay($('#SearchModal')); }
    else if (hit('[data-open-cart]') && drawer()) { e.preventDefault(); openCart(); }
    else if (hit('[data-close-cart]')) { if (!hit('a')) e.preventDefault(); closeOverlay(drawer()); }
    else if (hit('[data-close-modal]')) { e.preventDefault(); closeOverlay(hit('.modal')); }
    else if (hit('[data-size-guide]')) { e.preventDefault(); const pdp = hit('[data-product]'); sizeGuide(pdp && $('[data-size-table]', pdp)); }
    else if (hit('[data-unit]')) { unit = hit('[data-unit]').dataset.unit; try { localStorage.setItem('ur:unit', unit); } catch (err) { /* */ } const m = $('#SizeGuide'); if (m && m._src) sizeGuide(m._src); }
    else if (hit('[data-remove]')) { e.preventDefault(); const b = hit('[data-remove]'); changeLine(parseInt(b.dataset.remove, 10), 0, b.closest('.line')); }
    else if (hit('[data-toggle-recover]')) { e.preventDefault(); const l = $('#LoginCard'), r = $('#RecoverCard'); if (l && r) { const showR = r.hidden; r.hidden = !showR; l.hidden = showR; } }
    else if (hit('[data-address-toggle]')) { e.preventDefault(); const b = hit('[data-address-toggle]'); const el = document.getElementById(b.dataset.addressToggle); if (el) { el.hidden = !el.hidden; b.setAttribute('aria-expanded', String(!el.hidden)); } }
    else if (hit('[data-address-delete]')) {
      e.preventDefault(); const b = hit('[data-address-delete]');
      if (confirm(b.dataset.confirm || 'Delete this address?')) { const f = document.createElement('form'); f.method = 'post'; f.action = `/account/addresses/${b.dataset.addressDelete}`; f.innerHTML = '<input type="hidden" name="_method" value="delete">'; body.appendChild(f); f.submit(); }
    }
    else {
      const a = hit('a[href^="#"]');
      if (a && a.getAttribute('href').length > 1) { const target = document.getElementById(a.getAttribute('href').slice(1)); if (target) { e.preventDefault(); scrollToY(target.getBoundingClientRect().top + window.scrollY - 20); } }
    }
  });

  /* ---------- pointer: cursor + magnetic buttons ---------- */
  function initPointer() {
    if (!FINE || RM) return;
    const cur = $('.cursor');
    let mx = W / 2, my = H / 2, rx = mx, ry = my;
    addEventListener('mousemove', (e) => { mx = e.clientX; my = e.clientY; }, { passive: true });
    if (cur) {
      const dot = $('.cursor__dot', cur), ring = $('.cursor__ring', cur), label = $('.cursor__label', cur);
      document.addEventListener('mouseleave', () => cur.classList.add('is-hidden'));
      document.addEventListener('mouseenter', () => cur.classList.remove('is-hidden'));
      document.addEventListener('mouseover', (e) => {
        const lab = e.target.closest('[data-cursor]'), btn = e.target.closest('a, button, label, summary, select, input');
        if (lab && !(btn && lab.contains(btn) && btn !== lab)) { label.textContent = lab.dataset.cursor; cur.classList.add('is-label'); cur.classList.remove('is-link'); return; }
        cur.classList.remove('is-label'); cur.classList.toggle('is-link', !!btn);
      });
      loops.push(() => {
        rx = lerp(rx, mx, 0.2); ry = lerp(ry, my, 0.2);
        dot.style.transform = `translate3d(${mx}px,${my}px,0) translate(-50%,-50%)`;
        ring.style.transform = `translate3d(${rx.toFixed(1)}px,${ry.toFixed(1)}px,0) translate(-50%,-50%)`;
      });
    }
    loops.push(() => {
      $$('.mag').forEach((m) => {
        const r = m.getBoundingClientRect(); if (r.bottom < 0 || r.top > H) return;
        const dx = mx - (r.left + r.width / 2), dy = my - (r.top + r.height / 2);
        if (Math.hypot(dx, dy) < 110) m.style.transform = `translate3d(${(dx * 0.2).toFixed(1)}px,${(dy * 0.2).toFixed(1)}px,0)`;
        else if (m.style.transform) m.style.transform = '';
      });
    });
  }

  /* ---------- page transitions ---------- */
  function initTransitions() {
    const veil = $('.page-veil');
    if (!veil || !flag('transitions') || RM || S.designMode) { html.classList.remove('is-arriving'); return; }
    if (html.classList.contains('is-arriving')) {
      try { sessionStorage.removeItem('ur:t'); } catch (e) { /* no storage */ }
      requestAnimationFrame(() => requestAnimationFrame(() => html.classList.add('is-arrived')));
      setTimeout(() => html.classList.remove('is-arriving', 'is-arrived'), 700);
    }
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-no-transition') || a.hasAttribute('data-open-cart')) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || (url.pathname === location.pathname && url.hash)) return;
      if (/\/(cart|checkout|account\/logout|admin)/.test(url.pathname)) return;
      e.preventDefault();
      try { sessionStorage.setItem('ur:t', '1'); } catch (err) { /* ignore */ }
      veil.classList.add('is-active');
      setTimeout(() => { location.href = url.href; }, 260);
    });
    addEventListener('pageshow', (e) => { if (e.persisted) { veil.classList.remove('is-active'); html.classList.remove('is-arriving', 'is-arrived'); } });
  }

  /* ---------- preloader: the wordmark rises letter by letter ---------- */
  function preloader(done) {
    const pre = $('#Preloader');
    let seen = false;
    try { seen = !S.preloaderEveryVisit && sessionStorage.getItem('ur:pre') === '1'; } catch (e) { /* no storage */ }
    if (!pre || seen || RM || S.designMode || html.classList.contains('is-arriving')) { if (pre) pre.classList.add('is-skipped'); done(); return; }
    try { sessionStorage.setItem('ur:pre', '1'); } catch (e) { /* ignore */ }
    lock();
    const num = $('.preloader__count b', pre), t0 = performance.now(), dur = 1050;
    let finished = false;
    const finish = () => { if (finished) return; finished = true; pre.classList.add('is-done'); body.classList.remove('is-locked'); setTimeout(done, 200); };
    const step = (t) => { const p = clamp((t - t0) / dur, 0, 1), e = EO(p); if (num) num.textContent = Math.round(e * 100); pre.style.setProperty('--p', e.toFixed(3)); if (p < 1) requestAnimationFrame(step); else setTimeout(finish, 120); };
    requestAnimationFrame(step);
    setTimeout(finish, 2400);
  }

  /* ---------- addresses, recommendations ---------- */
  function initAddresses(root) {
    if (!window.Shopify || !window.Shopify.CountryProvinceSelector) return;
    $$('[data-address-country-select]', root).forEach((sel) => {
      if (!once(sel, 'init')) return;
      const pid = sel.dataset.addressProvinceId;
      new window.Shopify.CountryProvinceSelector(sel.id, pid, { hideElement: pid.replace('AddressProvince', 'AddressProvinceContainer') });
    });
  }
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (!once(el, 'init') || !el.dataset.url) return;
      try {
        const res = await fetch(el.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html'), fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; observe(el); sweepSpins(); initTurns(el); }
      } catch (e) { /* silent */ }
    });
  }

  /* =========================================================
     Boot
     ========================================================= */
  function initRoot(root = document) {
    sweepSpins();
    observe(root); initTurns(root); preloadG3D(root);
    initAnnouncement(root); initMarquee(root); initParallax(root); initScrollWords(root); initRails(root);
    initCollage(root); initStatements(root); initPassport(root); initCircle(root); initSet(root);
    initNameScene(root); initMasks(root); initProductBar(root);
    initAddresses(root); initRecommendations(root);
  }
  function boot() {
    initHeader(); initPointer(); initSearch(); initTransitions();
    initRoot(document);
    requestAnimationFrame(frame);
    S.initRoot = initRoot; S.tick = tick;
    if (g3debug()) S._turns = spins;                                       // ?g3d-debug: the live turns, for inspection
    S.ready = (ago = 0) => { body.classList.add('is-ready'); heroStart = performance.now() - ago; };
    preloader(() => S.ready());
    if (S.designMode) {
      document.addEventListener('shopify:section:load', (e) => initRoot(e.target));
      document.addEventListener('shopify:section:unload', (e) => sweepSpins(e.target));
      document.addEventListener('shopify:section:select', (e) => { if (e.target.querySelector('#CartDrawer')) openOverlay(drawer()); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.querySelector('#CartDrawer')) closeOverlay(drawer()); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
